import FormBox from '../common/FormBox/FormBox';
import { ChangeEvent, useEffect, useState } from 'react';
import BaseInput from 'components/common/BaseInput';
import PasswordInput from 'components/common/PasswordInput';
import PhoneNumberField from 'components/common/PhoneNumberField/PhoneNumberField';
import api from 'api';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import * as S from '../forms/Forms.styled';
import ReactGA from 'react-ga4';
import { isValidPassword } from 'utils/validations/password';
import PasswordStrengthIndicator from 'components/common/PasswordStrengthIndicator/PasswordStrengthIndicator';

const formEntries = [
    'firstname',
    'middlename',
    'lastname',
    'email',
    'phone_number',
    'password',
    'repeatPassword',
    'role',
];
export interface Data {
    firstname: string;
    middlename: string;
    lastname: string;
    email: string;
    phone_number: string;
    password: string;
    repeatPassword: string;
    role: string;
}

export const initData: Data = {
    firstname: '',
    middlename: '',
    lastname: '',
    email: '',
    phone_number: '',
    role: 'SUPER_ADMIN',
    password: '',
    repeatPassword: '',
};

interface Msg {
    firstname: string;
    lastname: string;
    email: string;
    phone_number: string;
    password: string;
    repeatPassword: string;
    role: string;
}

const initMsg: Msg = {
    firstname: '',
    lastname: '',
    email: '',
    phone_number: '',
    role: '',
    password: '',
    repeatPassword: '',
};

export interface Agent {
    firstname: string;
    lastname: string;
    middlename: string;
    role: string;
    email: string;
}

export const initAgent: Agent = {
    firstname: '',
    lastname: '',
    middlename: '',
    role: 'ADMIN',
    email: '',
};

export default function PersonInfo() {
    const { isAuthenticated } = useSelector((state: RootState) => state.auth);
    const [data, setData] = useState<Data>(initData);
    const [validationMsg, setValidationMsg] = useState<Msg>(initMsg);

    const loadData = (data: Data) => {
        data = { ...data, password: '', repeatPassword: '' };
        setData(data);
    };

    useEffect(() => {
        ReactGA.send({
            hitType: 'pageview',
            page: `${window.location.pathname}`,
            title: 'PersonalInfo',
        });
    }, []);

    useEffect(() => {
        if (isAuthenticated) api.tableInput.newFetchCandidateInput(loadData, 'User');
    }, [isAuthenticated]);

    // FIELD VALIDATION
    const validate = async (field: string, value: string, valid?: boolean) => {
        let validated = true;
        if (valid === true || valid === false) {
            validated = valid;
        } else {
            let tmp = validationMsg;
            if (!field && !value) {
                for (const [key, value] of Object.entries(data)) {
                    const index = formEntries.findIndex((x) => x === key);
                    if (index === -1) continue;
                    if (key !== 'middlename') {
                        let msg = '';
                        if ((key === 'password' || key === 'repeatPassword') && isAuthenticated) {
                            continue;
                        } else if (value === '' || value === null) {
                            msg = 'Field should not be left empty';
                            tmp = { ...tmp, [key]: msg };
                            setValidationMsg(tmp);
                            validated = false;
                        } else if (key === 'email') {
                            if (!validateEmail(key, value)) {
                                msg = 'Invalid email address';
                                tmp = { ...tmp, [key]: msg };
                                setValidationMsg(tmp);
                                validated = false;
                            }
                            if (!isAuthenticated)
                                try {
                                    await api.checkEmail.get(value);
                                } catch {
                                    msg = 'Email already in use';
                                    tmp = { ...tmp, [key]: msg };
                                    setValidationMsg(tmp);
                                    validated = false;
                                    console.log(msg);
                                }
                        } else if (!Object.prototype.hasOwnProperty.call(data, 'password')) {
                            msg = 'Field should not be left empty';
                            tmp = { ...tmp, password: msg, repeatPassword: msg };
                            setValidationMsg(tmp);
                            validated = false;
                        } else if (!Object.prototype.hasOwnProperty.call(data, 'repeatPassword')) {
                            msg = 'Field should not be left empty';
                            tmp = { ...tmp, repeatPassword: msg };
                            setValidationMsg(tmp);
                            validated = false;
                        } else if (key === 'password') {
                            if (!handlePassword(key, value)) {
                                msg = 'Invalid password';
                                tmp = { ...tmp, [key]: msg };
                                setValidationMsg(tmp);
                                validated = false;
                            }
                        } else if (key === 'repeatPassword') {
                            if (!handleConfirmPassword(key, value)) {
                                msg = 'Invalid confirm password';
                                tmp = { ...tmp, [key]: msg };
                                setValidationMsg(tmp);
                                validated = false;
                            }
                        }
                    }
                }
            } else {
                let msg = '';
                if (value === '') {
                    msg = 'Field should not be left empty';
                    validated = false;
                }
                tmp = { ...tmp, [field]: msg };
            }

            setValidationMsg(tmp);
        }
        return validated;
    };

    const handleUpdate = (field: string, value: string) => {
        if (field !== 'password' && field !== 'repeatPassword' && field !== 'middlename') {
            validate(field, value);
        }
        const tmp: Data = { ...data, [field]: value };
        setData(tmp);
    };

    const handlePassword = (field: string, value: string) => {
        handleUpdate(field, value);

        // Requirements are surfaced by the strength indicator, so we clear the
        // field message here. Submit is still gated: validate() blocks on the
        // boolean returned below.
        const tmp = { ...validationMsg, [field]: '' };
        setValidationMsg(tmp);

        return isValidPassword(value);
    };

    const handleConfirmPassword = (field: string, value: string) => {
        handleUpdate(field, value);
        let validated = true;
        let tmp = validationMsg;

        let msg = '';
        if (data.password !== value) {
            msg = 'Passwords do not match';
            validated = false;
            validate(field, value, validated);
        }
        tmp = { ...tmp, [field]: msg };

        setValidationMsg(tmp);

        return validated;
    };

    const validateEmail = (field: string, value: string) => {
        handleUpdate(field, value);
        let validated = true;
        let msg = '';
        let tmp = validationMsg;

        if (!value) {
            msg = 'Field should not be left empty';
            validated = false;
            //  validate(field, value, validated);
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)) {
            msg = 'Invalid email address';
            validated = false;
            //  validate(field, value, validated);
        }
        tmp = { ...tmp, [field]: msg };
        setValidationMsg(tmp);
        return validated;
    };

    return (
        <FormBox validation={validate} tableNames={['User']} data={[data]}>
            <S.Title>Personal Info</S.Title>
            <S.GridSection>
                <S.FlexColumn>
                    <BaseInput
                        sx={{}}
                        id="firstname"
                        label="First name"
                        type="text"
                        value={data.firstname}
                        onFocus={(e) => {
                            validate('firstname', e.target.value);
                        }}
                        onChange={(e) => {
                            handleUpdate('firstname', e.target.value);
                        }}
                        error={validationMsg.firstname !== ''}
                        helperText={validationMsg.firstname}
                    />
                </S.FlexColumn>
                <S.FlexColumn>
                    <BaseInput
                        sx={{}}
                        id="middlename"
                        label="Middle name"
                        type="text"
                        value={data.middlename}
                        onChange={(e) => {
                            handleUpdate('middlename', e.target.value);
                        }}
                    />
                </S.FlexColumn>
                <S.FlexColumn>
                    <BaseInput
                        id="lastname"
                        label="Last name"
                        type="text"
                        value={data.lastname}
                        onFocus={(e) => {
                            validate('lastname', e.target.value);
                        }}
                        onChange={(e) => {
                            handleUpdate('lastname', e.target.value);
                        }}
                        error={validationMsg.lastname !== ''}
                        helperText={validationMsg.lastname}
                    />
                </S.FlexColumn>
            </S.GridSection>
            <S.GridSection>
                <S.FlexColumn>
                    <BaseInput
                        id="email"
                        label="Email"
                        type="text"
                        value={data.email}
                        disabled={isAuthenticated}
                        onFocus={(e) => {
                            validate('email', e.target.value);
                        }}
                        onChange={(e) => {
                            handleUpdate('email', e.target.value);
                        }}
                        error={validationMsg.email !== ''}
                        helperText={validationMsg.email}
                    />
                </S.FlexColumn>
                <S.FlexColumn>
                    <PhoneNumberField
                        sx={{
                            '&.Mui-error': {
                                '.MuiOutlinedInput-notchedOutline ': {
                                    borderColor: '#ff2727',
                                },
                            },
                        }}
                        label="Phone"
                        fullWidth
                        value={data.phone_number || ''}
                        onFocus={(e) => {
                            validate('phone_number', e.target.value);
                        }}
                        onChange={(v: string) => {
                            handleUpdate('phone_number', v);
                        }}
                        error={validationMsg.phone_number !== ''}
                        helperText={validationMsg.phone_number}
                    />
                </S.FlexColumn>
            </S.GridSection>

            <div hidden={isAuthenticated}>
                <div className="form-card-content-divider"></div>
                <S.SubTitle>
                    <>Password</>
                </S.SubTitle>
                <S.GridSection>
                    <S.FlexColumn>
                        <PasswordInput
                            label={`Password`}
                            inputProps={{
                                autoComplete: 'off',
                                form: { autocomplete: 'off' },
                            }}
                            fullWidth
                            value={data.password}
                            onFocus={(e) => {
                                handlePassword('password', e.target.value);
                            }}
                            onChange={(v: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                handlePassword('password', v.target.value);
                            }}
                            error={validationMsg.password !== ''}
                            helperText={validationMsg.password}
                        />
                        <PasswordStrengthIndicator password={data.password} />
                    </S.FlexColumn>
                    <S.FlexColumn>
                        <PasswordInput
                            label={`Confirm password`}
                            inputProps={{
                                autoComplete: 'off',
                                form: { autocomplete: 'off' },
                            }}
                            fullWidth
                            value={data.repeatPassword}
                            onFocus={(e) => {
                                handleConfirmPassword('repeatPassword', e.target.value);
                            }}
                            onChange={(v: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                handleConfirmPassword('repeatPassword', v.target.value);
                            }}
                            error={validationMsg.repeatPassword !== ''}
                            helperText={validationMsg.repeatPassword}
                        />
                    </S.FlexColumn>
                </S.GridSection>
            </div>
        </FormBox>
    );
}
